import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage, Language } from '../contexts/LanguageContext';
import './LanguageSwitcher.css';

const languages: { code: Language; name: string; flag: string }[] = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'sr', name: 'Srpski', flag: '🇷🇸' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
];

const LanguageSwitcher: React.FC = () => {
  const { language, setLanguage } = useLanguage();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleLanguageChange = (langCode: Language) => {
    setLanguage(langCode);
    setIsDropdownOpen(false);
  };

  const currentLanguage = languages.find(lang => lang.code === language);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="language-switcher" ref={dropdownRef}>
      <div className="language-switcher__dropdown-container">
        <motion.button
          type="button"
          className="language-switcher__dropdown-trigger"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          aria-expanded={isDropdownOpen}
          aria-haspopup="listbox"
          whileHover={{ scale: 1.03 }}
          whileTap={{ scale: 0.97 }}
          title={currentLanguage?.name}
        >
          <span className="language-switcher__flag" aria-hidden>
            {currentLanguage?.flag}
          </span>
          <span className="language-switcher__arrow" aria-hidden>
            ▼
          </span>
        </motion.button>

        {isDropdownOpen && (
          <motion.div
            className="language-switcher__dropdown"
            role="listbox"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            {languages.map((lang) => (
              <button
                key={lang.code}
                type="button"
                role="option"
                aria-selected={language === lang.code}
                className={`language-switcher__dropdown-item ${language === lang.code ? 'language-switcher__dropdown-item--active' : ''}`}
                onClick={() => handleLanguageChange(lang.code)}
              >
                <span className="language-switcher__flag">{lang.flag}</span>
                <span className="language-switcher__name">{lang.name}</span>
                <span className="language-switcher__code">{lang.code.toUpperCase()}</span>
              </button>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default LanguageSwitcher;
